import { EditorPopover } from 'module/ui/components/atoms/rich-text-editor/editor-popover/popover';
import { getCoordinatesFromSelection } from 'module/ui/components/atoms/rich-text-editor/utils/editor-utils';
import { Editor } from '@tiptap/core';
import { portalWithAppContext } from 'components/organisms/app-context/portal-with-context';
import { CustomCommand } from './types';
import { AskAiCommand } from './types';
import {
  beforeAskAiCommandStart,
  getAiCommandActionHandler,
} from './utils/action-handler';
import { getSelectionAsJSON } from '../../../utils/editor-utils';
import {
  addAskAiSelectionHighlight,
  addAskAiSelectionHighlightCommand,
  removeAskAiSelectionHighlightCommand,
} from '../../aski-ai-selection-highlight/utils';
import { EnhanceWithNotesInput } from '../ui/enhance-with-notes-input';
import { expandSelectionPositionToIncludeEntireSentence } from '../utils/expand-selection-to-sentence';
import { getPositionSelectedByUserIfAny } from './utils/expand-to-sentence';

interface EnhanceWithNotesPopoverProps {
  editor: Editor;
  onPopoverClose: () => void;
  onSubmit: (notes: string) => void;
}
const EnhanceWithNotesPopover = ({
  editor,
  onPopoverClose,
  onSubmit,
}: EnhanceWithNotesPopoverProps) => {
  const popoverState = {
    visible: true,
    position: getCoordinatesFromSelection(editor, false),
  };

  return (
    <EditorPopover
      editor={editor}
      content={() => <EnhanceWithNotesInput onSubmit={onSubmit} />}
      popoverState={popoverState}
      popoverContentContainerClassName={'enhance-with-notes-input-popover'}
      canIgnoreOutsideClickFor={(target) => {
        return target.closest('.enhance-with-notes-input-popover') !== null;
      }}
      placement="bottom"
      hidePopover={onPopoverClose}
    />
  );
};
const createPortal = () => {
  const portalContainer = document.createElement('div');

  document.body.appendChild(portalContainer);

  return portalContainer;
};

type EnhanceWithNotesInputProps = {
  editor: Editor;
  onPopoverClose: () => void;
  onSubmit: (notes: string) => void;
};
const enhanceWithNotesInput = ({
  editor,
  onPopoverClose,
  onSubmit: onSubmitProp,
}: EnhanceWithNotesInputProps) => {
  const portalContainer = createPortal();
  const destroyPortal = () => {
    root.unmount();
    portalContainer.remove();
  };
  const onClose = () => {
    onPopoverClose();
    destroyPortal();
  };
  const onSubmit = (notes: string) => {
    destroyPortal();
    onSubmitProp(notes);
  };

  const root = portalWithAppContext({
    container: portalContainer,
    children: (
      <EnhanceWithNotesPopover
        editor={editor}
        onPopoverClose={onClose}
        onSubmit={onSubmit}
      />
    ),
  });
};

export const enhanceWithNotesCommand: CustomCommand = ({
  editor,
  tr,
  dispatch,
  options,
}) => {
  if (!dispatch) {
    return false;
  }
  const {
    from: initialFrom,
    to: initialTo,
    noTextSelected,
  } = getPositionSelectedByUserIfAny(editor);

  if (!noTextSelected) {
    addAskAiSelectionHighlight(editor, tr, initialFrom, initialTo);
  }

  const onPopoverClose = () => {
    editor
      .chainWithAskAiActionMeta()
      .command(removeAskAiSelectionHighlightCommand(initialFrom, initialTo))
      .run();
  };
  const onSubmit = (notes: string) => {
    const { from: updatedFromPosition, to: updatedToPosition } =
      expandSelectionPositionToIncludeEntireSentence(
        editor,
        initialFrom,
        initialTo,
      );

    if (!noTextSelected) {
      editor
        .chainWithAskAiActionMeta()
        .command(removeAskAiSelectionHighlightCommand(initialFrom, initialTo))
        .run();
    }

    const selectedContent = getSelectionAsJSON(
      editor,
      updatedFromPosition,
      updatedToPosition,
    );

    beforeAskAiCommandStart(editor, AskAiCommand.EnhanceWithMyNotes);
    if (!noTextSelected) {
      editor
        .chainWithAskAiActionMeta()
        .command(
          addAskAiSelectionHighlightCommand(
            updatedFromPosition,
            updatedToPosition,
          ),
        )
        .run();
    }

    const { onCancel, onComplete } = getAiCommandActionHandler(
      AskAiCommand.EnhanceWithMyNotes,
      editor,
      { from: initialFrom, to: initialTo },
      { from: updatedFromPosition, to: updatedToPosition },
      options,
    );

    options.processAskAiRequest({
      payload: {
        command: AskAiCommand.EnhanceWithMyNotes,
        content: selectedContent,
        selection: { from: updatedFromPosition, to: updatedToPosition },
        enhanceWithNotes: notes,
      },
      editor,
      onCancel: onCancel,
      onComplete: onComplete,
    });
  };

  enhanceWithNotesInput({
    editor,
    onPopoverClose,
    onSubmit,
  });

  return true;
};
